import { Profile, ProfileUserType, StoreType } from '../../models/ClientModels';
import { isNotNull } from '../../utils/utils';
import { memorize } from '../firebase/rxfireExtensions';

export const fullDisplayName = (profile: {
  firstName: string;
  lastName: string;
}) => {
  if (profile == null) {
    return 'PSL User';
  }
  return profile?.firstName + ' ' + profile?.lastName;
};

export const firstLetterOrEmoji = memorize((input: string) => {
  const code = input.codePointAt(0);
  if (code == null) {
    return '';
  }
  return String.fromCodePoint(code);
});

export const nicknameWithInitial = (
  profile: Pick<Profile, 'firstName' | 'lastName' | 'storeFrontName'>,
  options?: {
    noStoreFrontName?: boolean;
    noTrailingDot?: boolean;
    suffix?: string;
  },
) => {
  if (profile.storeFrontName && !options?.noStoreFrontName) {
    return profile.storeFrontName;
  }
  if (profile.lastName && profile.lastName[0]) {
    return (
      `${profile.firstName} ${firstLetterOrEmoji(
        profile.lastName,
      ).toUpperCase()}${options?.noTrailingDot ? '' : '.'}` +
      (options?.suffix ?? '')
    );
  }
  return profile.firstName + (options?.suffix ?? '');
};

export const profileDisplayTextForAvatarFallback = (
  profile: { firstName: string; lastName: string } | undefined,
) => {
  return [profile?.firstName, profile?.lastName]
    .filter(isNotNull)
    .map(x => x.charAt(0))
    .join('');
};

export const screenNameOrNicknameWithInitial = (
  profile: Partial<
    Pick<Profile, 'username' | 'firstName' | 'lastName' | 'storeFrontName'>
  >,
  options?: { preferUsername?: boolean; ignoreSpecialUsername?: boolean },
): string => {
  if (profile.storeFrontName && !options?.preferUsername) {
    return profile.storeFrontName;
  }
  if (
    profile.username &&
    (!profile.username.startsWith('psluser_') ||
      options?.ignoreSpecialUsername === false)
  ) {
    return profile.username;
  }
  if (profile.lastName && profile.lastName[0]) {
    return `${profile.firstName} ${firstLetterOrEmoji(
      profile.lastName,
    ).toUpperCase()}.`;
  }
  return profile.firstName!;
};

export const isSeller = (profile: Pick<Profile, 'userType'>) =>
  profile.userType === ProfileUserType.verifiedSeller ||
  profile.userType === ProfileUserType.seller;

export const isSellerProfile = isSeller;

export const isPslStaff = (profile: Pick<Profile, 'badges'>) =>
  profile.badges?.staff;

export const isActiveSeller = (profile: Pick<Profile, 'badges' | 'userType'>) =>
  isSeller(profile) && profile.badges?.activeSeller === true;

export const isProfileValid = <T extends Profile>(
  profile?: null | T,
): boolean =>
  Boolean(
    profile != null && profile.firstName && profile.lastName && profile.avatar,
  );

export const isCreatorProfile = (
  profile: Pick<Profile, 'userType' | 'storeType'> | null | undefined,
) =>
  profile != null &&
  isSellerProfile(profile) &&
  profile.storeType === StoreType.Creator;
